import React, { useEffect, useRef, useState } from 'react';

const ROLE_LABELS = {
  admin: 'Administrator',
  candidate: 'Candidate',
  employer: 'Employer',
  employer_staff: 'Employer Staff',
};

const getInitials = (user) => {
  const first = (user?.firstName || '').trim();
  const last = (user?.lastName || '').trim();
  if (first || last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || first.charAt(0).toUpperCase();
  }
  return (user?.email || '?').charAt(0).toUpperCase();
};

const getDisplayName = (user) => {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  return name || user?.email || 'Account';
};

const UserMenu = ({ user, onLogout }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-2 rounded-full border border-secondary-200 py-1 pl-1 pr-2 transition-colors hover:bg-secondary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu for ${getDisplayName(user)}`}
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 text-xs font-semibold text-white">
          {getInitials(user)}
        </span>
        <span className="hidden max-w-[10rem] truncate text-sm font-medium text-secondary-700 sm:inline">
          {user?.firstName || getDisplayName(user)}
        </span>
        <svg className="h-4 w-4 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Account"
          className="absolute right-0 z-50 mt-2 w-64 rounded-xl border border-secondary-200 bg-white p-1 shadow-modal"
        >
          <div className="border-b border-secondary-100 px-3 py-3">
            <p className="truncate text-sm font-semibold text-secondary-900">{getDisplayName(user)}</p>
            {user?.email && <p className="truncate text-xs text-secondary-500">{user.email}</p>}
            {user?.userType && (
              <span className="badge-neutral mt-2">{ROLE_LABELS[user.userType] || user.userType}</span>
            )}
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
            className="mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium text-secondary-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            Logout
          </button>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
